import { Injectable } from "@angular/core";
import { StreamId, StreamSpecs } from "@common/domain";
import {
  Actions,
  createEffect,
  EffectNotification,
  ofType,
  OnRunEffects
} from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { LocalConferenceStreamsListService } from "@youm-front/conference-stream/data-access";
import { RoomStreamVO } from "@youm-front/room/domain";
import { partition } from "lodash";
import { Observable } from "rxjs";
import { concatMap, exhaustMap, takeUntil } from "rxjs/operators";
import { LocalAudioConferenceStream } from "youm-lib/conference";
import { RoomActions } from "../actions";
import { RoomSelectors } from "../selectors";

@Injectable()
export class RoomLocalAudioStreamEffects implements OnRunEffects {
  readonly roomActiveStreams$ = this.store.pipe(
    select(RoomSelectors.roomActiveStreams)
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly localConferenceStreamsListService: LocalConferenceStreamsListService
  ) {}

  localAudioPausePlay$ = createEffect(
    () =>
      this.roomActiveStreams$.pipe(
        concatMap((_) => this.localAudioPausePlay(_))
      ),
    { dispatch: false }
  );

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>) {
    return this.actions$.pipe(
      ofType(RoomActions.init),
      exhaustMap(() =>
        resolvedEffects$.pipe(
          takeUntil(this.actions$.pipe(ofType(RoomActions.destroy)))
        )
      )
    );
  }

  private async localAudioPausePlay(roomStreamVOs: RoomStreamVO[]) {
    const localAudioFile = "localAudioFile";

    const localAudioStreams =
      this.localConferenceStreamsListService.streams.filter((_) =>
        StreamSpecs.isOfType(_.streamId, localAudioFile)
      ) as LocalAudioConferenceStream[];
    const localAudioStreamIds = roomStreamVOs
      .filter((_) => StreamSpecs.isOfType(_.value.streamId, localAudioFile))
      .map((_) => _.value.streamId);

    const [localAudioStreamsToStart, localAudioStreamsToPause] = partition(
      localAudioStreams,
      (_) => localAudioStreamIds.includes(_.streamId as StreamId)
    );

    localAudioStreamsToPause.forEach((_) => _.pause());

    for (const stream of localAudioStreamsToStart) {
      await stream.play();
    }
  }
}
